'use client';

/**
 * Explore — browse the library by craft.
 *
 * Layout (matches design prototype):
 *  - Hero: eyebrow "Browse the library" + serif headline "Find prompts by craft."
 *  - Category tiles: 5/8 tiles with hue-based radial gradient + count + Browse CTA
 *  - Section: "This week · Rising prompts" + trending grid (8 cards)
 */

import Link from 'next/link';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { api } from '@/lib/api';
import { Prompt } from '@/types';
import PromptGrid from '@/components/prompt/PromptGrid';
import { useT } from '@/lib/i18n';

/**
 * Display metadata for each canonical backend category.
 * hue drives the tile's radial gradient. icon is Material Symbols name.
 */
const CATEGORY_META: Record<string, { hue: number; icon: string; blurb: string }> = {
  Writing:   { hue: 285, icon: 'edit_note',       blurb: 'Prose, copy, fiction, scripts.' },
  Coding:    { hue: 230, icon: 'code',            blurb: 'Refactors, reviews, debugging.' },
  Art:       { hue: 20,  icon: 'palette',         blurb: 'Midjourney, DALL·E, style prompts.' },
  Business:  { hue: 160, icon: 'business_center', blurb: 'Marketing, sales, operations.' },
  Education: { hue: 100, icon: 'school',          blurb: 'Tutors, lesson plans, explainers.' },
};
const CATEGORY_ORDER = ['Writing', 'Coding', 'Art', 'Business', 'Education'];

type CatCount = { category: string; count: number };

export default function ExplorePage() {
  const router = useRouter();
  const t = useT();
  const [cats, setCats] = useState<CatCount[]>([]);
  const [rising, setRising] = useState<Prompt[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.stats.categories(),
      api.prompts.list({ sort: 'top_rated', size: 8 }),
    ])
      .then(([catRes, promptRes]) => {
        setCats(catRes);
        setRising(promptRes.items);
      })
      .catch(() => { setCats([]); setRising([]); })
      .finally(() => setLoading(false));
  }, []);

  // Merge backend counts with display metadata, keeping canonical order; tiles
  // missing from the DB still render (count = 0) so the grid shape is stable.
  const tiles = CATEGORY_ORDER.map((name) => {
    const found = cats.find((c) => c.category === name);
    return { name, count: found?.count ?? 0, ...CATEGORY_META[name] };
  });

  return (
    <div>
      {/* ── Hero ─────────────────────────────────────────── */}
      <section className="page-pad explore-hero" style={{ paddingTop: 56, paddingBottom: 8, maxWidth: 1200 }}>
        <div style={{
          fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.12em',
          color: 'var(--ink-3)', marginBottom: 10, fontWeight: 500,
        }}>
          {t('explore.eyebrow')}
        </div>
        <h1 style={{
          fontFamily: 'var(--font-serif)',
          fontSize: 'clamp(36px, 4.4vw, 52px)',
          fontWeight: 400,
          fontVariationSettings: '"opsz" 110, "SOFT" 50',
          letterSpacing: '-0.03em',
          lineHeight: 1.05,
          margin: 0,
          color: 'var(--ink)',
          textWrap: 'balance',
        }}>
          {t('explore.title_before')}{' '}
          <em style={{ fontStyle: 'italic', color: 'var(--accent)', fontWeight: 400 }}>
            {t('explore.title_em')}
          </em>
          {t('explore.title_after')}
        </h1>
        <p style={{
          color: 'var(--ink-2)', marginTop: 14, maxWidth: 540,
          fontSize: 15, lineHeight: 1.55, marginBottom: 0,
        }}>
          {t('explore.tagline')}
        </p>
      </section>

      {/* ── Category tiles ───────────────────────────────── */}
      <div className="page-pad" style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
        gap: 16,
        paddingTop: 28,
        paddingBottom: 48,
      }}>
        {tiles.map((c) => (
          <button
            key={c.name}
            onClick={() => router.push(`/library/${c.name.toLowerCase()}`)}
            className="cat-tile"
          >
            {/* Blurred gradient visual — bleeds to the corner. */}
            <div
              className="cat-vis"
              style={{
                background: `radial-gradient(circle at 60% 40%, oklch(0.78 0.18 ${c.hue}), transparent 68%)`,
              }}
            />

            <div
              className="cat-icon-chip"
              style={{
                background: `oklch(0.94 0.05 ${c.hue})`,
                color: `oklch(0.35 0.14 ${c.hue})`,
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 18 }}>
                {c.icon}
              </span>
            </div>

            <div>
              <div className="cat-name">{c.name}</div>
              <div className="cat-count">
                {loading ? '—' : `${c.count.toLocaleString()} prompt${c.count === 1 ? '' : 's'}`}
              </div>
            </div>

            <div className="cat-cta">
              {t('explore.browse')}
              <span className="material-symbols-outlined" style={{ fontSize: 14 }}>
                arrow_forward
              </span>
            </div>
          </button>
        ))}
      </div>

      {/* ── Rising section ───────────────────────────────── */}
      <div className="section-hd">
        <div>
          <div className="eyebrow">{t('explore.rising_eyebrow')}</div>
          <h2>{t('explore.rising_title')}</h2>
        </div>
        <Link href="/trending" className="ps-btn ps-btn-ghost ps-btn-sm no-underline">
          {t('explore.view_all')}
          <span className="material-symbols-outlined ps-icon">arrow_forward</span>
        </Link>
      </div>

      <div className="page-pad" style={{ paddingBottom: 64 }}>
        <PromptGrid prompts={rising} loading={loading} />
      </div>
    </div>
  );
}
